/**
 * Markup del Top 10 — `/cvn-top10.html` (Fase 5g.3).
 *
 * Ranking del escuadrón por media de calificación LSO, proyectado sobre
 * la pantalla del proyector de la ready room. El nº 1 va destacado con
 * foto de perfil y callsign grandes; del 2 al 10, una lista compacta.
 *
 * Datos de `GET /api/greenie-board` (media por piloto) cruzados con
 * `GET /api/pilots` (foto del nº 1). Solo entran pilotos con media
 * computable (`avgGradePoints !== null`).
 */

import type { GreenieBoardPilot } from '@51esc/shared';

/** Una posición del ranking. */
export interface Top10Entry {
  rank: number;
  callsign: string;
  /** Media de puntos LSO (garantizada no-null). */
  avg: number;
  traps: number;
}

/**
 * Ordena los pilotos por media descendente (desempate por nº de traps)
 * y devuelve las 10 primeras posiciones. Descarta a los que no tienen
 * media computable.
 */
export function computeTop10(pilots: GreenieBoardPilot[]): Top10Entry[] {
  const ranked = pilots
    .filter(
      (p): p is GreenieBoardPilot & { avgGradePoints: number } =>
        p.avgGradePoints !== null,
    )
    .sort(
      (a, b) =>
        b.avgGradePoints - a.avgGradePoints || b.trapCount - a.trapCount,
    );
  return ranked.slice(0, 10).map((p, i) => ({
    rank: i + 1,
    callsign: p.callsign,
    avg: p.avgGradePoints,
    traps: p.trapCount,
  }));
}

// ─── Render ────────────────────────────────────────────────────────────

/** Shell estático: cabecera + zona de cuerpo. */
export function renderTop10Shell(): string {
  return `
    <div class="ct-stage">
      <section class="ct-inner" aria-label="Top 10 apontajes">
        <header class="ct-head">
          <h1 class="ct-title">TOP 10 · APONTAJES</h1>
          <p class="ct-sub">Ranking por media de calificación LSO — CVN-51</p>
        </header>
        <div class="ct-body" data-zone="body"></div>
      </section>
    </div>
  `;
}

/**
 * Cuerpo del ranking: bloque destacado del nº 1 + lista del 2 al 10.
 * `avatars` mapea callsign en minúsculas → URL de la foto de perfil.
 */
export function renderTop10(
  entries: Top10Entry[],
  avatars: Map<string, string>,
): string {
  const champion = entries[0];
  const rest = entries.slice(1);
  return (
    (champion ? renderChampion(champion, avatars) : '') +
    (rest.length > 0
      ? `<ol class="ct-list">${rest.map(renderRow).join('')}</ol>`
      : '')
  );
}

function renderChampion(e: Top10Entry, avatars: Map<string, string>): string {
  const url = avatars.get(e.callsign.toLowerCase());
  const photo = url
    ? `<img class="ct-champ-photo" src="${esc(url)}" alt="${esc(e.callsign)}" />`
    : `<div class="ct-champ-photo ct-champ-photo--empty" aria-hidden="true">` +
      `${esc(e.callsign.slice(0, 1).toUpperCase())}</div>`;
  return `
    <div class="ct-champ">
      ${photo}
      <div class="ct-champ-info">
        <span class="ct-champ-rank">★ Nº 1</span>
        <span class="ct-champ-name">${esc(e.callsign)}</span>
        <span class="ct-champ-score">${e.avg.toFixed(2)}<small> pts media</small></span>
        <span class="ct-champ-traps">${String(e.traps)} traps</span>
      </div>
    </div>
  `;
}

function renderRow(e: Top10Entry): string {
  return `
    <li class="ct-row">
      <span class="ct-rank">${String(e.rank)}</span>
      <span class="ct-name">${esc(e.callsign)}</span>
      <span class="ct-traps">${String(e.traps)} traps</span>
      <span class="ct-score">${e.avg.toFixed(2)}</span>
    </li>
  `;
}

/** Nota centrada para los estados de carga / vacío / error. */
export function renderTop10Note(message: string, isError = false): string {
  return `<p class="ct-note${isError ? ' ct-note--error' : ''}">${esc(message)}</p>`;
}

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
